/*@flow*/
/** @jsx jsx */
import {
  useState,
  useReducer,
  useContext,
  createContext,
  useEffect,
  memo,
  Fragment,
  forwardRef,
} from 'react';
import * as t from 'lib/t';
import hash from 'lib/hash';
import mobilecheck from 'lib/mobilecheck';
import setMinutes from 'date-fns/setMinutes';
import setHours from 'date-fns/setHours';
import setSeconds from 'date-fns/setSeconds';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import differenceInDays from 'date-fns/differenceInDays';
import setIn from 'lib/setIn';
import NonEmptyString from 'lib/NonEmptyString';
import NonEmptyArray from 'lib/NonEmptyArray';
import NonEmptyList from 'lib/NonEmptyList';
import simulate from 'lib/simulate';
import * as I from 'immutable';
import toIsoStringWithOffset from 'lib/toIsoStringWithOffset';
import { css, jsx } from '@emotion/core';
import DatePicker from 'comps/DatePicker';
import cx from 'classnames';
import RecurrenceType from 'lib/resource/types/Recurrence';
import qs from 'qs';
import omit from 'ramda/src/omit';

const mkTimestamp = toIsoStringWithOffset;
const mkUTCDate = (date: Date) => {
  const now_utc = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  );
  return new Date(now_utc);
};
const mkDate = (value) => (value ? new Date(value) : new Date());
const dateAtHour = (h) => {
  const v = new Date();
  const fn = (s, arr) => arr[0](s, arr[1]);
  return [[setHours, h], [setMinutes, 0], [setSeconds, 0]].reduce(fn, v);
};
const mkStartDate = () => new Date();
const mkEndDate = () => new Date();

const Days = {
  MO: 'Mon.',
  TU: 'Tues.',
  WE: 'Wed.',
  TH: 'Thurs.',
  FR: 'Fri.',
  SA: 'Satur.',
  SU: 'Sun.',
};
const normalizeDigit = (d) => (d < 0 ? 6 + d : d);
const getDayFromDigit = (d) => Object.keys(Days)[normalizeDigit(d - 1)];

const getFullDayName = (v) => Days[v];

type RRuleSet = {
  dtstart: string,
  dtend: string,
  rrule: {
    freq: 'DAILY' | 'WEEKLY' | 'MONTHLY',
    until?: string,
    count?: number,
    byday?: $Keys<typeof Days>[],
  },
};

type ActionT = {
  type: 'ADD' | 'REMOVE' | 'SET_RECORD',
  payload: any,
};

type ContextT = {
  state: I.List<RRuleSet>,
  dispatch: DispatchO<ActionT>,
};

const RecurContext = createContext<ContextT>({});

const mkRule = () => ({
  dtstart: mkTimestamp(mkStartDate()),
  dtend: mkTimestamp(mkEndDate()),
  rrule: {
    freq: 'DAILY',
    count: 1,
  },
});

type State = I.List<RRuleSet>;

const reducer = (state: State, action: ActionT) => {
  const { payload } = action;
  let newState = state;
  switch (action.type) {
    case 'ADD':
      if (payload.dtend) {
        newState = state.concat(payload);
      }
      break;
    case 'REMOVE':
      newState = state.delete(payload);
      break;
    case 'SET_RECORD':
      if (payload) {
        newState = setIn(newState)(payload);
      }
      break;
    default:
      break;
  }

  return newState;
};

const commands = (ctx) => {
  return {
    newRule() {
      simulate((document.body: any), 'mousedown');
      ctx.dispatch({ type: 'ADD', payload: mkRule() });
    },
    remove(index: number) {
      ctx.dispatch({
        type: 'REMOVE',
        payload: index,
      });
    },
    clear(index: number) {
      commands(ctx).remove(index);
      commands(ctx).newRule();
    },
    setUntil(v: Date, index: number) {
      commands(ctx).set(`${index}.rrule`, (v2) => {
        if (v) {
          v2 = omit(['count'])(v2);
          return { ...v2, until: mkTimestamp(v) };
        }
        return omit(['until'])(v2);
      });
    },
    setRepeats(v: boolean, index: number) {
      commands(ctx).set(`${index}.rrule`, (v2) => {
        if (v) {
          const v3 = omit(['count'])(v2);
          return { ...v3, until: mkTimestamp(mkDate()) };
        }
        const v3 = omit(['until', 'byday'])(v2);
        return { ...v3, freq: 'DAILY', count: 1 };
      });
    },
    init(v: State) {
      commands(ctx).set('', v);
    },
    set(path: string, value: any) {
      ctx.dispatch({
        type: 'SET_RECORD',
        payload: { path, value },
      });
    },
  };
};

const DayList = ({ byday, index, startDate, endDate }) => {
  const ctx = useContext(RecurContext);
  const isDayChecked = (v) => {
    return byday && byday.includes(v);
  };

  const startDateDay = startDate.getDay();
  const numDays = differenceInDays(endDate, startDate);

  const onCheckDay = (v) => {
    commands(ctx).set(`${index}.rrule.byday`, (v2) => {
      const v4 = v2 || [];
      return v4.includes(v)
        ? I.List(v4)
            .filterNot((v3) => v3 === v)
            .toJS()
        : v4.concat(v);
    });
  };

  useEffect(() => {
    const day = getDayFromDigit(startDateDay);
    if (!byday) {
      commands(ctx).set(`${index}.rrule.byday`, [day]);
    }
  }, []);

  const isDisabled = numDays > 0;

  return (
    <div className="mt-2">
      <label className="font-weight-light">Days</label>
      <div className="d-md-flex" data-testid="DayList">
        {Object.keys(Days).map((v) => (
          <label key={v} className="mr-3 d-block" data-testid={'Day-' + v}>
            <input
              type="checkbox"
              value={v}
              disabled={isDisabled}
              checked={isDayChecked(v)}
              onChange={(e) => onCheckDay(e.target.value)}
            />{' '}
            {getFullDayName(v)}
          </label>
        ))}
      </div>
    </div>
  );
};

const SelectFrequency = (props) => {
  const ctx = useContext(RecurContext);
  const { byday = [], index, value } = props;

  let PERIODS = {
    DAILY: 'Daily',
    WEEKLY: 'Weekly',
    MONTHLY: 'Monthly',
    YEARLY: 'Yearly',
  };

  const numOccurrencesPerWeek = byday.length;

  const initPeriods = numOccurrencesPerWeek < 1 ? ['DAILY'] : [];
  const availablePeriods = initPeriods.concat(['WEEKLY', 'MONTHLY', 'YEARLY']);

  const setFreq = (v) => commands(ctx).set(`${index}.rrule.freq`, v);

  useEffect(() => {
    numOccurrencesPerWeek > 1 && value === 'DAILY' && setFreq('WEEKLY');
  }, [numOccurrencesPerWeek]);

  return (
    <select
      className="form-control"
      value={value}
      onChange={(e) => setFreq(e.target.value)}
      data-testid="SelectFrequency"
    >
      {availablePeriods.map((v) => (
        <option key={v} value={v}>
          {PERIODS[v]}
        </option>
      ))}
    </select>
  );
};

const RepeatRules = memo(({ rrule, index, startDate, endDate }) => {
  const ctx = useContext(RecurContext);
  const { byday } = rrule;
  const showByDay = rrule.freq && rrule.freq !== 'DAILY';
  let untilDate = new Date(rrule.until || '');

  useEffect(() => {
    if (isBefore(untilDate, endDate)) {
      commands(ctx).setUntil(endDate, index);
    }
  }, [endDate]);

  return (
    <div className="align-items-stretch mt-2">
      <div className="d-md-flex">
        <div className="col-lg-6 p-0 mr-3">
          <label className="font-weight-light">Repeats</label>
          <SelectFrequency
            value={rrule.freq}
            index={index}
            byday={byday || []}
          />
        </div>
        <div className="col-lg-6 p-0">
          <label className="font-weight-light">Until When?</label>
          <div>
            <InputDate
              value={untilDate}
              data-testid="UntilDateInput"
              minDate={endDate}
              onChange={(v) => {
                commands(ctx).setUntil(v, index);
              }}
            />
          </div>
        </div>
      </div>
      {showByDay && (
        <DayList
          byday={byday}
          index={index}
          startDate={startDate}
          endDate={endDate}
        />
      )}
    </div>
  );
});

const CustomInput = forwardRef(
  ({ value, ...rest }: { value?: string, 'data-testid'?: string }, ref) => (
    <a
      {...rest}
      className="border-bottom d-block p-0 text-left w-100 form-control bg-transparent"
      ref={ref}
      css={css('box-shadow: none !important;')}
    >
      {value}
    </a>
  )
);

const DateTimeWrapper = (props: {
  value: any,
  onChange: Function,
  dateFormat: string,
  showTimeSelect?: boolean,
  selectsStart?: boolean,
  selectsEnd?: boolean,
  startDate?: Date,
  endDate?: Date,
  shouldCloseOnSelect?: boolean,
  'data-testid'?: string,
}) => {
  const [value, setValue] = useState(props.value);
  useEffect(() => {
    if (props.shouldCloseOnSelect === false) return;
    if (value !== props.value) {
      props.onChange(value);
    }
  }, [value]);

  const isMobile = mobilecheck();

  return (
    <div
      css={css(`
      .react-datepicker__input-container, .react-datepicker-wrapper {
        width: 100%;
      }
      .react-datepicker__time-list-item {
        line-height: 2rem;
      }
      --datepicker-font-ratio: 1.5;

      .react-datepicker {
        font-size: calc(0.8rem * var(--datepicker-font-ratio));
      }
      .react-datepicker__current-month,
      .react-datepicker-time__header,
      .react-datepicker-year-header {
        font-size: calc(0.944rem * var(--datepicker-font-ratio));
      }

      .react-datepicker__portal .react-datepicker__current-month,
      .react-datepicker__portal .react-datepicker-time__header {
        font-size: calc(1.44rem * var(--datepicker-font-ratio));
      }
      .react-datepicker__navigation {
        line-height: calc(1.7rem * var(--datepicker-font-ratio))
      }
      .react-datepicker__day-name,
      .react-datepicker__day,
      .react-datepicker__time-name,
      .react-datepicker__week-number {
        width: calc(1.7rem * var(--datepicker-font-ratio));
        line-height: calc(1.7rem * var(--datepicker-font-ratio));
        margin: calc(0.166rem * var(--datepicker-font-ratio));
      }
      .react-datepicker__year-option,
      .react-datepicker__month-option,
      .react-datepicker__month-year-option {
        line-height: 2rem;
      }
      .react-datepicker__navigation {
        line-height: calc(1.7rem * var(--datepicker-font-ratio));
      }
      .react-datepicker__time-container, .react-datepicker__time-box {
        width: 87px !important;
      }
      .react-datepicker__navigation--next--with-time:not(.react-datepicker__navigation--next--with-today-button) {
        right: 98px !important;
      }
      .react-datepicker__time-box {
        font-size: 1.1rem !important;
      }
    `)}
    >
      <DatePicker
        {...props}
        customInput={<CustomInput data-testid={props['data-testid']} />}
        selected={value}
        onChange={(v) => setValue(v)}
        className="form-control"
        withPortal={isMobile}
        onClickOutside={() => {
          props.onChange(value);
        }}
      />
    </div>
  );
};

const InputDateTime = (props) => {
  return (
    <DateTimeWrapper
      {...props}
      dateFormat={props.showTimeSelect ? 'Pp' : 'MM/dd/yyyy'}
      shouldCloseOnSelect={false}
    />
  );
};

const InputDate = (props) => {
  return <DateTimeWrapper {...props} dateFormat="MM/dd/yyyy" />;
};

const HasCount = t.subtype(t.Object, (v) => v.count && v.count > 1);
const HasUntil = t.subtype(t.Object, (v) => NonEmptyString.is(v.until));
const RepeatType = t.union([HasCount, HasUntil]);

const Rule = ({ value, index }: { value: RRuleSet, index: number }) => {
  const ctx = useContext(RecurContext);
  const { dtstart, dtend, rrule } = value;
  const sd = mkDate(dtstart);
  const ed = mkDate(dtend);

  const isRepeated = RepeatType.is(rrule);

  const [startDate, setStartDate] = useState(sd);
  const [endDate, setEndDate] = useState(ed);

  useEffect(() => {
    commands(ctx).set(`${index}.dtstart`, mkTimestamp(startDate));
  }, [startDate]);

  useEffect(() => {
    commands(ctx).set(`${index}.dtend`, mkTimestamp(endDate));
  }, [endDate]);

  let errors = null;

  const validator = t.validate(value, RecurrenceType);

  if (!validator.isValid()) {
    errors = validator.errors.map((v, i) => (
      <div className="text-danger" key={i}>
        {v.message}
      </div>
    ));
  }
  const hasError = (key) => validator.errors.some((v) => v.path.includes(key));

  return (
    <div
      className="border-bottom pt-1 pl-2 pr-2 pb-1"
      data-testid={`Rule-${index}`}
      css={css(`
        background: #fbfbfb;
        select, input, option { background: #fbfbfb !important; }
        input[type="date"]::-webkit-clear-button,
        input[type="time"]::-webkit-clear-button { display: none; }
      `)}
    >
      <div>
        <div className="col-lg-8 p-0 mr-3">
          <label
            className={cx('font-weight-light', {
              'text-danger': hasError('dtstart'),
            })}
          >
            Start
          </label>
          <div>
            <InputDateTime
              key={mkTimestamp(startDate)}
              value={startDate}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              data-testid="StartDateTimeInput"
              showTimeSelect
              onChange={(v) => {
                if (!v) return;
                setStartDate(v);
                if (isAfter(v, endDate)) {
                  setEndDate(v);
                }
              }}
            />
          </div>
        </div>
        <div className="col-lg-8 p-0">
          <label
            className={cx('font-weight-light', {
              'text-danger': hasError('dtend'),
            })}
          >
            End
          </label>
          <div>
            <InputDateTime
              key={mkTimestamp(endDate)}
              value={endDate}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              data-testid="EndDateTimeInput"
              showTimeSelect
              onChange={(v) => {
                if (!v) return;
                setEndDate(v);
                if (isBefore(v, startDate)) {
                  setStartDate(v);
                }
              }}
            />
          </div>
        </div>
      </div>
      {isRepeated && rrule && (
        <RepeatRules
          rrule={rrule}
          index={index}
          selectsEnd
          startDate={startDate}
          endDate={endDate}
        />
      )}
      <label
        data-testid="RepeatBox"
        className={cx('mt-2 font-weight-light', { 'mb-0': index !== 0 })}
      >
        <input
          type="checkbox"
          checked={isRepeated}
          onChange={(e) => {
            commands(ctx).setRepeats(e.target.checked, index);
          }}
        />{' '}
        This event repeats
      </label>

      <div className="d-flex">
        {index !== 0 && (
          <a
            onClick={() =>
              index !== 0
                ? commands(ctx).remove(index)
                : commands(ctx).clear(index)
            }
            className="font-weight-light pb-1 ml-auto"
            data-testid="RemoveTime"
          >
            Remove time
          </a>
        )}
      </div>
      {errors}
    </div>
  );
};

const HiddenInputFields = ({ namespace }) => {
  const { state } = useContext(RecurContext);
  if (!namespace) return null;

  // Serialize component state as hidden input fields
  const formValues = qs.parse(
    qs.stringify({ [namespace]: { recurrences: state.toJS() } }),
    { depth: 0 }
  );
  const container = formValues[namespace];
  if (!container) return null;

  return Object.keys(container).map((v) => (
    <input type="hidden" name={namespace + v} key={v} value={container[v]} />
  ));
};

type PropsT = {
  namespace?: string,
  value: RRuleSet[],
  onChange?: (RRuleSet[]) => any,
};

const noop = (..._args: any) => {};
const getInit = (v) => I.List(NonEmptyArray.is(v) ? v : []);

const Component = ({ namespace, value = [], onChange = noop }: PropsT) => {
  // Initialize the rules from value of this field
  const [state, dispatch] = useReducer(reducer, getInit(value));
  const ctx = { state, dispatch };

  useEffect(() => {
    const v = I.List(value);
    // Invoke init with every change to value
    if (hash(v) === hash(state)) return;
    // ...unles value is empty
    if (v.isEmpty()) return;
    // Ensure the value has the correct type
    const validator = t.validate(value, t.list(RecurrenceType));
    if (validator.isValid()) {
      commands(ctx).init(v);
    } else {
      // throw new Error();
      console.error(validator.errors);
    }
  }, [hash(value)]);

  useEffect(() => {
    // Invoke onChange with every change to a rule
    if (hash(value) !== hash(state)) {
      const v: RRuleSet[] = (state.toJS(): any);
      onChange(v);
    }
  }, [hash(state)]);

  return (
    <RecurContext.Provider value={ctx}>
      <Fragment>
        {state.map((v, i) => (
          <Rule value={v} key={JSON.stringify(v) + '-' + i} index={i} />
        ))}
        <a
          className={cx('text-info p-2 d-block', {
            'mt-1': NonEmptyList.is(state),
          })}
          onClick={() => commands(ctx).newRule()}
          data-testid="AddAnotherTime"
        >
          <i className="fa fa-plus" /> Add another time for this event
        </a>
        <HiddenInputFields namespace={namespace} />
      </Fragment>
    </RecurContext.Provider>
  );
};

export default Component;
